// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import {
    Assignees,
    DdjDocument,
    DdjDocumentCore,
    DdjTemplate,
    DocumentStatus,
    IdVerification,
    IdVerificationUtilizationModel,
    HashCompareRequest,
    ImpactPrinterFields,
    IntellicheckIdUpload,
    InternalDocumentType,
    InternalDocumentTypeIds,
    PddjQueue,
    PdfField,
    StandardDocument,
    TemplateGroup,
    ProductIds,
    TemplateUploadType,
    FormDocumentUploadType,
} from '@interfaces/document.interfaces';
import { UserPermissionIds, UserTypes } from '@interfaces/user.interfaces';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { base64ToBlob } from '../utils/pdf-form-utils';
import { sortByDisplayName } from '../utils/utils';
import { ApiService } from './api.service';
import { FeatureFlagService } from './feature-flag.service';
import { UserService } from './user.service';
import { IDScanningSessionLog } from '../shared/intellicheck-upload/id-scan-logging';
import { RpcApiError, RpcApiResponse } from '@interfaces/api.interfaces';
import { FileData } from '@interfaces/interfaces';
import { AssigneeToDocStatus } from '../utils/document-utils';

@Injectable({ providedIn: 'root' })
export class DocumentsService implements OnDestroy {
    public pddjDocument: PddjQueue = null;
    public pddjDocumentQueueSessionCount = 0;
    requestedDocId: number = null;
    public pddjDocumentQueue$: BehaviorSubject<PddjQueue[]> = new BehaviorSubject<PddjQueue[]>([]);
    private _dealId$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    private _docs$: BehaviorSubject<DdjDocument[]> = new BehaviorSubject<DdjDocument[]>(null);
    private _templates$: BehaviorSubject<DdjTemplate[]> = new BehaviorSubject<DdjTemplate[]>(null);
    private _coreTemplates$: BehaviorSubject<DdjDocumentCore[]> = new BehaviorSubject<DdjDocumentCore[]>(null);
    private _internalDocumentTypes$: BehaviorSubject<InternalDocumentType[]> = new BehaviorSubject<InternalDocumentType[]>(null);
    impactPrinterData: ImpactPrinterFields;
    isComponentAlive = true;
    // because we use doc-form and sign sign components and the methods I need
    // for savve and continue button are not accessible from each
    documentSaveAndContinue: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    apiSigningEnabled: boolean;
    constructor(
        private readonly apiService: ApiService,
        private readonly userService: UserService,
        private readonly router: Router,
        private readonly featureFlag: FeatureFlagService
    ) {
        (<any>window).chrome?.webview?.addEventListener('message', (event) => {
            if (event.data) {
                this.pddjDocument = null;
                this.impactPrinterData = null;

                try {
                    this.pddjDocument = { type: '', name: '', blob: null, impact: null };
                    this.pddjDocument.blob = base64ToBlob(event.data);
                    this.addToPddjQueue('pdf');
                    this.pddjRouting();
                } catch (exception) {
                    try {
                        this.ImpactPrinterEventHander(event.data);
                        this.pddjRouting();
                    } catch (e) {
                        console.error('Unexpected json data from webview', e);
                        console.error('Unexpected data from webview', exception);
                    }
                }
            }
        });

        (<any>window).chrome?.webview?.postMessage({ pddj: true });
        this.apiSigningEnabled = this.featureFlag.getDefaultFeatureConfiguration('apiSigning');
    }

    get dealId(): number {
        return this._dealId$.value;
    }

    get documents(): DdjDocument[] {
        return this._docs$.value || [];
    }

    get pddjDocumentQueue(): PddjQueue[] {
        return this.pddjDocumentQueue$.value;
    }

    set pddjDocumentQueue(value: PddjQueue[]) {
        this.pddjDocumentQueue$.next(value);
    }

    docs$(): Observable<DdjDocument[]> {
        return this._docs$.asObservable();
    }

    templates$(): Observable<DdjTemplate[]> {
        return this._templates$.asObservable();
    }

    coreTemplates$(): Observable<DdjDocumentCore[]> {
        return this._coreTemplates$.asObservable();
    }

    internalDocumentTypes$(): Observable<InternalDocumentType[]> {
        return this._internalDocumentTypes$.asObservable();
    }

    getInternalDocumentTypeById(docTypeId: InternalDocumentTypeIds) {
        return this._internalDocumentTypes$.value.find((type) => type.internalDocumentTypeId === docTypeId);
    }

    getDocById(id: number): DdjDocument {
        return this.documents.find((doc) => doc.documentId === id);
    }

    getDocByInternalTypeId(typeId: InternalDocumentTypeIds) {
        return this.documents.find((doc) => doc.internalDocumentTypeId === typeId);
    }

    getReadyForSignatureByDocId(id: number) {
        return this.documents.find((doc) => doc.documentId === id)?.readyForSignatures;
    }

    getUploadTypeIdByDocId(id: number) {
        return this.documents.find((doc) => doc.documentId === id)?.uploadTypeId;
    }

    // Dealer will only be able to sign finalize documents if everything else is completed
    finalizeEnabledForDealer() {
        const filtered = this.documents.filter(
            (x) =>
                x.internalDocumentTypeId !== InternalDocumentTypeIds.BuyerFinalSignatureDocument &&
                x.internalDocumentTypeId !== InternalDocumentTypeIds.CoBuyerFinalSignatureDocument
        );
        const enableFinalDocs = !filtered.some(
            (x) =>
                x.documentStatusId === DocumentStatus.BuyerQueue ||
                x.documentStatusId === DocumentStatus.CoBuyerQueue ||
                x.documentStatusId === DocumentStatus.DealerQueue ||
                x.documentStatusId === DocumentStatus.Needed ||
                x.documentStatusId === DocumentStatus.Draft
        );
        return enableFinalDocs;
    }

    // Verify the assignee has completed their part on any documents.
    // We do not care if other assignee still need to complete their part anymore
    finalizeEnabledForCustomer(assignee: Assignees): boolean {
        // filter out any final documents
        const docs = this.documents.filter(
            (x) =>
                x.internalDocumentTypeId !== InternalDocumentTypeIds.BuyerFinalSignatureDocument &&
                x.internalDocumentTypeId !== InternalDocumentTypeIds.CoBuyerFinalSignatureDocument &&
                x.documentStatusId !== DocumentStatus.Deleted
        );

        // gather the fields names once to make loop easier
        const needsSignature = assignee === Assignees.buyer ? 'needsBuyerSignature' : 'needsCoBuyerSignature';
        const needsUpload = assignee === Assignees.buyer ? 'needsBuyerUpload' : 'needsCoBuyerUpload';
        const signedBy = assignee === Assignees.buyer ? 'buyerSignedBy' : 'coBuyerSignedBy';

        for (const doc of docs) {
            // Verify the assignee has signed/uploaded everywhere that is requested
            if (
                (doc.requirements[needsSignature] && !doc.status[signedBy]) ||
                (doc.requirements[needsUpload] && doc.documentStatusId !== DocumentStatus.Complete)
            ) {
                return false;
            }
        }

        return true;
    }

    getDiscardedDocs() {
        return sortByDisplayName(
            this.documents.filter(
                (doc) =>
                    doc.documentStatusId === DocumentStatus.Rejected ||
                    doc.documentStatusId === DocumentStatus.Cancelled ||
                    doc.documentStatusId === DocumentStatus.Deleted
            )
        );
    }

    getCompleteDocs() {
        return sortByDisplayName(this.documents.filter((doc) => doc.documentStatusId === DocumentStatus.Complete));
    }

    getCoBuyerPending() {
        return sortByDisplayName(this.documents.filter((doc) => doc.documentStatusId === DocumentStatus.CoBuyerQueue));
    }

    getCustomerPending() {
        return sortByDisplayName(this.documents.filter((doc) => doc.documentStatusId === DocumentStatus.BuyerQueue));
    }

    getDealerPending() {
        return sortByDisplayName(
            this.documents.filter((doc) => doc.documentStatusId === DocumentStatus.DealerQueue || doc.documentStatusId === DocumentStatus.Draft)
        );
    }

    //Please don't delete this as it still needs to be implemented
    //getDocumentLink(fileName: string): string {
    //	 //DDJ-814
    //	return (fileName && (fileName.endsWith('.jpeg') || fileName.endsWith('.jpg'))) ? 'view' : fileName ? 'sign' : 'upload';
    //}

    // Keep this as just the API call. If this is getting called in multiple spots we
    // will refactor those sections of the app as we need to be able to consistenly get
    // a fresh document list as a user is signing the documents
    getDocsList$(dealId: number): Observable<DdjDocument[]> {
        if (dealId !== this.dealId) {
            this._dealId$.next(dealId);
        }

        return this.apiService.getV2$<any[]>(`api/deal/${dealId}/document/list/${this.userService.user.dealerId}`).pipe(
            map((response) => {
                if (response?.data) {
                    //change the json string to fields
                    response.data.forEach((doc) => {
                        if (
                            doc.inputFieldSettings !== null &&
                            doc.inputFieldSettings !== undefined &&
                            doc.inputFieldSettings !== '{}' &&
                            doc.inputFieldSettings !== ''
                        ) {
                            const inputFields: { [key: string]: PdfField } = JSON.parse(doc.inputFieldSettings);
                            doc.inputFieldSettings = inputFields;
                        }
                    });
                    return sortByDisplayName(response.data);
                } else {
                    return [];
                }
            }),
            tap((docs) => {
                this._docs$.next(docs);
            })
        );
    }

    getDocLastQueueById(dealId: number, dealerId: number, docId: number): Observable<{ documentStatus: DocumentStatus; queueName: string }> {
        return this.apiService
            .getV2$<{ documentStatus: DocumentStatus; queueName: string }>(`api/deal/${dealId}/document/${docId}/lastQueue/${dealerId}`)
            .pipe(map((response) => response.data));
    }

    getIdVerifications$(dealId: number, dealerId: number): Observable<IdVerification[]> {
        return this.apiService.getV2$<IdVerification[]>(`api/deal/${dealId}/document/idVerificationResults/${dealerId}`).pipe(
            map((response) => {
                return response.data?.map((d) => new IdVerification(d));
            })
        );
    }

    getIdVerificationUtilization$(dealerId: number): Observable<IdVerificationUtilizationModel[]> {
        const today = new Date();
        const dd = String(today.getDate()).padStart(2, '0');
        const mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
        const yyyy = today.getFullYear();

        const asofDate = yyyy + '-' + mm + '-' + dd;
        return this.apiService.getV2$<IdVerificationUtilizationModel[]>(`api/dealer/admin/idVerificationUtilization/${dealerId}/date/${asofDate}`).pipe(
            map((response) => {
                return response.data;
            })
        );
    }

    // can this dealer submit scans for verification?
    getDealerIdVerificationScanStatus(dealerId: number): Observable<boolean> {
        return this.getIdVerificationUtilization$(dealerId).pipe(
            switchMap((results) => {
                if (0 === results.length) {
                    // results only exist if there are scans this month.  if there's no results, there's no restrictions on this dealer
                    return of(true);
                }

                const additionalPackDetails = results.find((dl) => dl.productId == ProductIds.DriversLicenseScan);
                if (null !== additionalPackDetails) {
                    if (additionalPackDetails.currentlyActive) {
                        // with the additional scan product enabled, there are no limits to what the dealer can do
                        return of(true);
                    }

                    // if the additional scan product is disabled, but there's an override set, the override takes precidence over the default limits
                    if (null !== additionalPackDetails.overrideLimit) {
                        return of(additionalPackDetails.overrideLimit > additionalPackDetails.numberOfScans);
                    }
                }

                // are we within the base limit?
                const baseUsage = results.find((dl) => dl.productId === ProductIds.EzDocsBase);
                if (null !== baseUsage) {
                    const limit = baseUsage.numberOfScansAllowed;
                    const remainingScans = limit - baseUsage.numberOfScans;

                    if (remainingScans > 0) {
                        return of(true);
                    }
                }

                // this dealer has no remaining scans available
                return of(false);
            })
        );
    }

    submitForValidation$(dealerId: number, dealId: number, documentId: number): Observable<boolean> {
        return this.apiService
            .postV3$<boolean>(`api/deal/${dealId}/file/intellicheck/${documentId}/${dealerId}/process`, null)
            .pipe(map((response) => response.data));
    }

    getIdVerificationThresholds$(): Observable<any> {
        return this.apiService.getV2$<any>('api/deal/idVerificationThreshold', null, false).pipe(
            map((response: { data: any; error?; message? }) => {
                if (response) {
                    if (response.error) {
                        console.error('response.error', response.error);
                        return {
                            ...response.error,
                            error: true,
                        };
                    } else if (response.data) {
                        return response.data;
                    }
                }
            })
        );
    }

    private createGroup(document: any): TemplateGroup {
        return {
            groupName: document.groupName,
            templates: [document.templateDocumentId],
            templateGroupId: document.templateGroupId,
        };
    }

    private addToGroup(document: any, group: any) {
        const index = group.templates.findIndex((doc) => doc === document.templateDocumentId);
        index === -1 ? group.templates.push(document.templateDocumentId) : null;
    }

    getTemplateGroupNames$(): Observable<TemplateGroup[]> {
        return this.apiService.getV2$<TemplateGroup[]>(`api/template/group/templateGroupNames/${this.userService.user.dealerId}`).pipe(
            map((response) => {
                const groups = [];

                response.data.forEach((doc) => {
                    const checkIfGroupExists = groups.find((group) => group.groupName === doc.groupName);

                    !checkIfGroupExists ? groups.push(this.createGroup(doc)) : this.addToGroup(doc, checkIfGroupExists);
                });

                return groups;
            })
        );
    }

    getTemplateGroup$(templateGroupId: number): Observable<TemplateGroup[]> {
        return this.apiService.getV2$<TemplateGroup[]>(`api/template/group/${this.userService.user.dealerId}/${templateGroupId}`).pipe(
            map((response) => {
                const groups = [];

                response.data.forEach((doc) => {
                    const checkIfGroupExists = groups.find((group) => group.groupName === doc.groupName);

                    !checkIfGroupExists ? groups.push(this.createGroup(doc)) : this.addToGroup(doc, checkIfGroupExists);
                });

                return groups;
            })
        );
    }

    getCommmonTemplateList$(): Observable<DdjTemplate[]> {
        return this.apiService.getV2$<DdjTemplate[]>('api/template/commonlist').pipe(map((response) => this.fixTemplateList(response.data)));
    }

    getCommmonTemplateDealerAssociationsList$(dealerId: number): Observable<number[]> {
        return this.apiService
            .getV2$<DdjTemplate[]>(`api/template/common/dealerCommonDocument/${dealerId}`)
            .pipe(map((res) => res.data.map((x) => x.documentId)));
    }

    updateCommmonTemplateDealerAssociations$(dealerId: number, documentIds: number[]): Observable<number[]> {
        return this.apiService
            .postV2$<number[]>(`api/template/common/dealerCommonDocument/${dealerId}`, {
                documentIds: documentIds.toString(),
            })
            .pipe(map((res) => res.data));
    }

    addRemoveCommonDocumentV2$(documents: string[], dealerId: number): Observable<boolean> {
        const formData = { documents: documents, dealerId: dealerId };
        return this.apiService.postV2$<boolean>('api/template/commontemplates', formData).pipe(map((response) => response.data));
    }

    getInternalDocumentTypes$(): Observable<InternalDocumentType[]> {
        return this.apiService.getV2$<InternalDocumentType[]>('api/document/internalDocumentTypes').pipe(
            map((types) => types.data),
            tap((intTypes) => this._internalDocumentTypes$.next(intTypes))
        );
    }

    getFilePathByDocId(dealId: number, docId: number): Observable<FileData> {
        if (dealId !== this.dealId) {
            this._dealId$.next(dealId);
        }

        return this.apiService.getV2$<FileData>(`api/deal/${dealId}/file/${docId}/${this.userService.user.dealerId}`).pipe(
            map((response) => {
                return response?.data;
            })
        );
    }

    getSignedData$(path: string): Observable<Blob> {
        return this.apiService.getSignedBlob$(path);
    }

    getSignedFileByPath$(dealId: number, docId: number): Observable<RpcApiResponse<FileData>> {
        if (dealId !== this.dealId) {
            this._dealId$.next(dealId);
        }

        return this.getFileData$(docId, `api/deal/${dealId}/file/${docId}/${this.userService.user.dealerId}`);
    }

    getTemplateByDocId(docId: number) {
        return this._templates$.value.find((template) => template.documentId === docId);
    }

    getSignedTemplateFileByDocId$(docId: number): Observable<any> {
        return this.getFileData$(docId, `api/template/file/dealerId/${this.userService.user.dealerId}/${docId}`);
    }

    getCommonTemplateFileByDocId$(docId: number): Observable<any> {
        return this.getFileData$(docId, `api/template/file/common/${docId}`);
    }

    getTemplateList$(adminDealerId: number, filterResult = true): Observable<DdjTemplate[]> {
        return this.apiService.getV2$<DdjTemplate[]>(`api/template/list/${this.userService.user.dealerId}`, adminDealerId).pipe(
            map((response) => {
                if (response?.data === undefined || response?.data === null || response.data.length < 1) {
                    console.error('No Document List Available');
                }

                if (filterResult) {
                    const isInternal =
                        this.userService.user.userType === UserTypes.Internal &&
                        (this.userService.user.dealerAppPermissions || [])
                            .find((x) => x.dealerId === this.userService.user.dealerId)
                            .userPermissionId.some((x) => x === UserPermissionIds.DDJAdmin);
                    const isDDJAdmin =
                        this.userService.user.userType === UserTypes.Dealer &&
                        (this.userService.user.dealerAppPermissions || [])
                            .find((x) => x.dealerId === this.userService.user.dealerId)
                            .userPermissionId.some((x) => x === UserPermissionIds.DDJAdmin);

                    const filtered = response.data.filter((tempDoc) => {
                        if (tempDoc.systemDocument) {
                            return isInternal || isDDJAdmin;
                        } else {
                            return true;
                        }
                    });
                    return this.fixTemplateList(filtered);
                } else {
                    return this.fixTemplateList(response.data);
                }
            }),
            tap((templates) => this._templates$.next(templates))
        );
    }

    getFullTemplateData$(documentId: number, withFile = false, adminDealerId: number = null): Observable<DdjTemplate> {
        return this.apiService.getV2$<any>(`api/template/fullTemplate/${this.userService.user.dealerId}/${documentId}/${withFile}`, adminDealerId).pipe(
            map((response) => {
                if (response?.data === undefined || response?.data === null) {
                    console.error('No Document Available');
                }

                const fixedTemplate = this.fixTemplate(response?.data);
                fixedTemplate.documentSignedURL = response?.data.file?.filename;
                return fixedTemplate;
            })
        );
    }

    getTemplateBasicList$(filterResult = true, adminDealerId = null): Observable<DdjDocumentCore[]> {
        return this.apiService.getV2$<DdjDocumentCore[]>(`api/template/basicList/${this.userService.user.dealerId}`, adminDealerId).pipe(
            map((response) => {
                if (response?.data === undefined || response?.data === null || response.data.length < 1) {
                    console.error('No Document List Available');
                }

                if (filterResult) {
                    const isInternal =
                        this.userService.user.userType === UserTypes.Internal &&
                        (this.userService.user.dealerAppPermissions || [])
                            .find((x) => x.dealerId === this.userService.user.dealerId)
                            .userPermissionId.some((x) => x === UserPermissionIds.DDJAdmin);
                    const isDDJAdmin =
                        this.userService.user.userType === UserTypes.Dealer &&
                        (this.userService.user.dealerAppPermissions || [])
                            .find((x) => x.dealerId === this.userService.user.dealerId)
                            .userPermissionId.some((x) => x === UserPermissionIds.DDJAdmin);

                    const filtered = response.data.filter((tempDoc) => {
                        if (tempDoc.systemDocument) {
                            return isInternal || isDDJAdmin;
                        } else {
                            return true;
                        }
                    });
                    return <DdjDocumentCore[]>sortByDisplayName(filtered);
                } else {
                    return <DdjDocumentCore[]>sortByDisplayName(response.data);
                }
            }),
            tap((templates) => this._coreTemplates$.next(templates))
        );
    }

    getUiDetails$(uiPage?: string): Observable<TemplateUploadType[]> {
        return this.apiService.getUi$<TemplateUploadType[]>('api/template/uploadTypes', uiPage).pipe(map((response) => response?.data));
    }

    downloadDocs$(dealId: number, allDocs = false, docIds?: number[]) {
        return this.apiService.getV2$<{ docs?: FileData[]; dealId?: string }>(
            `api/deal/${dealId}/document/download/${this.userService.user.dealerId}?allDocs=${allDocs}&docIds=${docIds ? docIds.toString() : ''}`
        );
    }

    downloadLinks$(links: FileData[]): Observable<HTMLAnchorElement[]> {
        //Please don't delete this as it still needs to be implemented
        const downloadLinks: Array<HTMLAnchorElement> = [];
        links.forEach((link) => {
            //const byteString = window.atob(link.content);
            //const arrayBuffer = new ArrayBuffer(byteString.length);
            //const unitArray = new Uint8Array(arrayBuffer);

            //for (let i = 0; i < byteString.length; i++) {
            //    unitArray[i] = byteString.charCodeAt(i);
            //}

            //var blob = new Blob([unitArray], { type: 'application/pdf' });

            const downloadLink = document.createElement('a');
            downloadLink.target = '_blank';
            downloadLink.href = link.filename; // window.URL.createObjectURL(blob);
            downloadLink.download = `${link.filename}`.split('/').slice(4) + '';
            downloadLinks.push(downloadLink);
        });

        return of(downloadLinks);
    }

    isEnableSigning(document: DdjDocument, assignee: Assignees, signingMetaData: [{ [key: string]: PdfField }] | null = null): boolean {
        // In the case an invalid signature is stored, we reset the document to undefined
        if (!document) {
            return false;
        }

        const reqs = document?.requirements;
        const status = document.status;
        switch (assignee) {
            case Assignees.buyer:
                return (
                    document.fileName &&
                    reqs.needsBuyerSignature &&
                    !status.buyerSignedBy &&
                    !this.determineAssigneeHasSignedByMetaData(signingMetaData, Assignees.buyer)
                );
            case Assignees.cobuyer:
                return (
                    document.fileName &&
                    reqs.needsCoBuyerSignature &&
                    !status.coBuyerSignedBy &&
                    !this.determineAssigneeHasSignedByMetaData(signingMetaData, Assignees.cobuyer)
                );
            default:
                return (
                    document.fileName &&
                    reqs.needsDealerSignature &&
                    !status.dealerSignedBy &&
                    !this.determineAssigneeHasSignedByMetaData(signingMetaData, Assignees.dealer)
                );
        }
    }

    assigneeHasSignAndStatusIsTheirQueue(document: DdjDocument, assignee: Assignees, signingMetaData: [{ [key: string]: PdfField }] | null): boolean {
        if (document.uploadTypeId === FormDocumentUploadType.Dealer_Uploaded_with_Predefine_Signatures) {
            return false;
        }

        if (!this.isEnableSigning(document, assignee, signingMetaData) && document.documentStatusId === AssigneeToDocStatus(assignee)) {
            return true;
        }

        return false;
    }

    determineAssigneeHasSignedByMetaData(signedMetaData: [{ [key: string]: PdfField }], currentAssignee: Assignees): boolean {
        let hasSigned: boolean = false;
        const currentAssigneeString = currentAssignee.toLocaleLowerCase();

        for (const metaKey of signedMetaData) {
            hasSigned = Object.keys(metaKey).some(
                (x) =>
                    x.startsWith(`${currentAssigneeString}_SIGNATURE`) ||
                    x.startsWith(`${currentAssigneeString}_REQUIRED`) ||
                    x.startsWith(`${currentAssigneeString}_OPTIONAL`)
            );

            if (hasSigned) {
                return hasSigned;
            }
        }

        return hasSigned;
    }

    numberOfOpenDocs(): number {
        return this._docs$.getValue().filter((doc) => doc.isClosed === false).length;
    }

    public isClosedStatus(status: DocumentStatus) {
        return [DocumentStatus.Deleted, DocumentStatus.Complete].includes(status);
    }

    //Final docs no longer have to be on a deal to close. Only checking if the deal is close to closing
    haveFinalDocsBeenUnlocked() {
        const ddj = this.documents.filter(
            (doc) =>
                doc.documentStatusId === DocumentStatus.DealerQueue ||
                doc.documentStatusId === DocumentStatus.Draft ||
                doc.documentStatusId === DocumentStatus.BuyerQueue ||
                doc.documentStatusId === DocumentStatus.CoBuyerQueue
        );

        return ddj.length <= 2;
    }

    createDealDocument$(formData: FormData, dealId: number, fileData: FormData): Observable<DdjDocument> {
        formData.delete('file');
        formData.append('file', new Blob(), '.pdf');
        return this.apiService
            .postV3$(`api/deal/${dealId}/document`, formData)
            .pipe(map((response) => this.uploadContent(<DdjDocument>response.data, fileData)));
    }

    createDealDocumentV2$(formData: FormData, dealId: number, notify?: boolean, preventDocReset?: boolean): Observable<DdjDocument> {
        if (!preventDocReset) {
            let docs: DdjDocument[];
            this._docs$.next(docs);
        }

        return this.apiService
            .postV3$<DdjDocument>(`api/deal/${dealId}/document${notify !== undefined ? '?notify=' + notify : ''}`, formData)
            .pipe(map((response) => response?.data));
    }

    addDocumentsToDeal(templateDocumentIds: number[], dealerId: number, dealId: number): Observable<boolean> {
        return this.apiService
            .postV3$<boolean>(`api/deal/${dealId}/addDocumentToDeal`, {
                templateDocumentIds: templateDocumentIds,
                dealerId: dealerId,
            })
            .pipe(map((response) => response.data));
    }

    createTemplate$(formData: FormData): Observable<DdjTemplate> {
        return this.apiService.postV2$<DdjTemplate>('api/template', formData).pipe(map((response) => response.data));
    }

    // when changing to C# api remove this this.createGroup() from the custom-deal-group.component.ts
    // the actual group will be returned, instead of an array of templates
    createOrUpdateTemplateGroup$(groupName: string, dealerId: number, groupId: number, templates: number[]): Observable<any> {
        return this.apiService
            .postV3$('api/template/group/', {
                groupName: groupName,
                dealerId: dealerId,
                templateGroupId: groupId,
                templateDocumentIds: templates,
            })
            .pipe(map((response) => response.data));
    }

    deleteDocument$(dealerId: number, dealId: number, documentId: number): Observable<DdjDocument[]> {
        const formData = new FormData();
        formData.append('userFields', JSON.stringify({ dealerId }));
        return this.apiService.deleteV3$<DdjDocument[]>(`api/deal/${dealId}/document/delete/${documentId}`, formData).pipe(
            map((response) => response?.data),
            tap((docs) => this._docs$.next(docs))
        );
    }

    restoreDocument$(dealId: number, documentId: number, formData: FormData): Observable<DdjDocument[]> {
        return this.apiService.postV3$<DdjDocument[]>(`api/deal/${dealId}/document/restore/${documentId}`, formData).pipe(
            map((response) => response?.data),
            tap((docs) => this._docs$.next(docs))
        );
    }

    deleteTemplate$(formData: FormData): Observable<DdjDocumentCore[]> {
        return this.apiService.postV2$<DdjDocumentCore[]>('api/template/delete', formData).pipe(
            map((response) => sortByDisplayName<DdjDocumentCore>(response.data)),
            tap((templates) => this._coreTemplates$.next(templates))
        );
    }

    deleteCommonTemplate$(formData: FormData): Observable<DdjDocumentCore[]> {
        return this.apiService.postV2$<DdjDocumentCore[]>('api/template/delete/common', formData).pipe(map((response) => response.data));
    }

    deleteTemplateGroup$(templateGroupId: number): Observable<any> {
        return this.apiService.postV3$(`api/template/group/${templateGroupId}/${this.userService.user.dealerId}`, null).pipe(map((response) => response.data));
    }

    uploadDocumentFileV2$(dealId: number, docId: number, json: string): Observable<DdjDocument> {
        return this.apiService
            .putV2$(`api/deal/${dealId}/file/${docId}/${this.userService.user.dealerId}`, json, {
                AccessControlOrigin: '*',
                'Content-Type': 'application/json',
            })
            .pipe(
                catchError((httpResponse) => {
                    return of({ ...httpResponse });
                }),
                map((response: any) => {
                    if (response?.error) {
                        return {
                            error: true,
                            ...response.error,
                        };
                    } else {
                        return response ? response.data : response;
                    }
                })
            );
    }

    ////TODO:DDJ-2369 08-31-2023 Needs to be removed as no longer needed and still calls nodejs
    ////Left in as PDDJ code needs to be removed which is obsolete
    //uploadDocumentFile$(dealId: number, docId: number, formData: FormData, fileData: any): Observable<DdjDocument> {
    //    formData.delete('file');
    //    formData.append('file', new Blob(), '.pdf');
    //    return this.apiService
    //        .put$(`api/deal/${dealId}/file/${docId}/${this.userService.user.dealerId}`, formData)
    //        .pipe(map((response) => this.uploadContent(<DdjDocument>response.data, fileData)));
    //}

    uploadFileUnauthenticated$(body: string): Observable<DdjDocument> {
        return this.apiService
            .putV2$(
                'api/deal/documentJacket/updateFile',
                body,
                {
                    AccessControlOrigin: '*',
                    'Content-Type': 'application/json',
                },
                false
            )
            .pipe(
                catchError((httpResponse) => {
                    return of({ ...httpResponse });
                }),
                map((response: any) => {
                    if (response?.error) {
                        return {
                            error: true,
                            ...response.error,
                        };
                    } else {
                        return response ? response.data : response;
                    }
                })
            );
    }

    intellicheckUpload$(dealId: number, docId: number, body: IntellicheckIdUpload): Observable<DdjDocument | RpcApiError> {
        return this.apiService
            .putV2$<DdjDocument>(
                `api/deal/${dealId}/file/ident/${docId}/${this.userService.user.dealerId}`,
                JSON.stringify(body),
                {
                    AccessControlOrigin: '*',
                    'Content-Type': 'application/json',
                },
                false
            )
            .pipe(
                catchError((httpResponse) => {
                    return of({ ...httpResponse });
                }),
                map((response: RpcApiResponse<DdjDocument>) => {
                    if (response?.error) {
                        return {
                            error: true,
                            ...response.error,
                        };
                    } else {
                        return response?.data;
                    }
                })
            );
    }

    intellicheckTokenUpload$(body: IntellicheckIdUpload): Observable<DdjDocument | RpcApiError> {
        return this.apiService
            .putV2$<DdjDocument>(
                'api/deal/documentJacket/uploadIdent',
                JSON.stringify(body),
                {
                    AccessControlOrigin: '*',
                    'Content-Type': 'application/json',
                },
                false
            )
            .pipe(
                catchError((httpResponse) => {
                    return of({ ...httpResponse });
                }),
                map((response: RpcApiResponse<DdjDocument>) => {
                    if (response?.error) {
                        return {
                            error: true,
                            ...response.error,
                        };
                    } else {
                        return response?.data;
                    }
                })
            );
    }

    uploadIdScanningSessionLog$(scanningSessionLog: IDScanningSessionLog): Observable<any> {
        const json = JSON.stringify(scanningSessionLog);
        if (scanningSessionLog.token) {
            return this.apiService
                .putV2$(
                    'api/idscanlogging/token',
                    json,
                    {
                        AccessControlOrigin: '*',
                        'Content-Type': 'application/json',
                    },
                    false
                )
                .pipe(
                    catchError((httpResponse) => {
                        return of({ ...httpResponse });
                    }),
                    map((response: any) => {
                        if (response?.error) {
                            return {
                                error: true,
                                ...response.error,
                            };
                        } else {
                            return response ? response.data : response;
                        }
                    })
                );
        }
        return this.apiService
            .putV2$(
                'api/idscanlogging',
                json,
                {
                    AccessControlOrigin: '*',
                    'Content-Type': 'application/json',
                },
                false
            )
            .pipe(
                catchError((httpResponse) => {
                    return of({ ...httpResponse });
                }),
                map((response: any) => {
                    if (response?.error) {
                        return {
                            error: true,
                            ...response.error,
                        };
                    } else {
                        return response ? response.data : response;
                    }
                })
            );
    }

    impactFlattenFile$(json: string): Observable<DdjDocument> {
        return this.apiService
            .postV2$('api/Document/impactFlatten', json, {
                AccessControlOrigin: '*',
                'Content-Type': 'application/json',
            })
            .pipe(map((response) => <DdjDocument>response.data));
    }

    addSignatures$(unused: number, unused2: number, unused3: FormData, unused4: any, json: string): Observable<DdjDocument> {
        let docs: DdjDocument[];
        this._docs$.next(docs);

        return this.apiService
            .postV3$<DdjDocument>(
                'api/Document/sign',
                json,
                {
                    AccessControlOrigin: '*',
                    'Content-Type': 'application/json',
                },
                false
            )
            .pipe(
                map((response) => {
                    if (!response || !('data' in response)) {
                        throw response?.error;
                    }

                    return response?.data;
                })
            );
    }

    flattenDoc$(pdfJson: string): Observable<Blob> {
        return this.apiService
            .postV2$<any>('api/Document/flattenDoc', pdfJson, {
                AccessControlOrigin: '*',
                'Content-Type': 'application/json',
            })
            .pipe(
                map((response) => {
                    if (response.data !== null) {
                        const byteCharacters = atob(response.data);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }

                        const byteArray = new Uint8Array(byteNumbers);
                        return new Blob([byteArray], { type: 'application/pdf' });
                    }

                    return null;
                })
            );
    }

    updateTemplateDocumentInfo$(dealerId: number, templateDocId: number, offSetX: number, offSetY: number): Observable<DdjTemplate> {
        const formData: FormData = new FormData();
        formData.append(
            'documentFields',
            JSON.stringify({
                documentId: templateDocId,
                offsetX: offSetX,
                offsetY: offSetY,
            })
        );

        return this.apiService.postV2$(`api/template/updateTemplate/${dealerId}`, formData).pipe(map((response) => <DdjTemplate>response.data));
    }

    uploadContent(response: DdjDocument, formData: FormData): DdjDocument {
        if (response.documentSignedURL && response.documentSignedURL != '') {
            firstValueFrom(this.apiService.s3put$(response.documentSignedURL, formData));
        }
        return response;
    }

    private fixTemplateList(templates: DdjTemplate[]) {
        return <DdjTemplate[]>sortByDisplayName(
            templates.map((tmpl) => {
                return this.fixTemplate(tmpl);
            })
        );
    }

    private fixTemplate(tmpl: DdjTemplate) {
        if (typeof tmpl?.inputFieldSettings === 'string') {
            try {
                tmpl.inputFieldSettings = JSON.parse(tmpl.inputFieldSettings);
            } catch (e) {
                // Ignore error
            }
        }
        return tmpl;
    }

    private getFileData$(docId, apiPath): Observable<RpcApiResponse<any>> {
        this.requestedDocId = docId;
        return this.apiService.getV2$(apiPath);
    }

    private ImpactPrinterEventHander(data) {
        this.impactPrinterData = data;

        if (
            this.impactPrinterData !== null &&
            (this.impactPrinterData.DocumentData !== null ||
                (this.impactPrinterData.DocumentDataLines !== null && this.impactPrinterData.DocumentDataLines.length > 0) ||
                (this.impactPrinterData.rawBase64 !== null &&
                    this.impactPrinterData.rawBase64.length > 0 &&
                    this.impactPrinterData.MetaDataPrinter?.advancedPageSettings !== null))
        ) {
            this.pddjDocument.impact = this.impactPrinterData;
            this.addToPddjQueue('impact');
            return;
        }

        this.impactPrinterData = null;
        throw 'Printer Job Has No Data. Please Try To Print Again.';
    }

    pddjRouting() {
        const splitRoute = this.router.url.split('/');
        switch (splitRoute[splitRoute.length - 1]) {
            case 'deals':
            case 'done':
            case 'upload':
                //stay on page
                break;

            default:
                this.router.navigate(['/pddj', 'deals']);
        }
    }

    addToPddjQueue(type: string) {
        this.pddjDocument.type = type;
        this.pddjDocumentQueue.push(this.pddjDocument);
        this.pddjDocument = null;
        this.updatePddjQueueCount(true);
        this.pddjDocumentQueue[this.pddjDocumentQueue.length - 1].name = `Print Job ${this.pddjDocumentQueueSessionCount} : ${type}`;
    }

    pddjQueueVerbiage(queueHasDocuments?: boolean, queue?: PddjQueue[]): string {
        if (!queueHasDocuments || !queue) {
            queue = this.pddjDocumentQueue;
            queueHasDocuments = queue.some((x) => x);
        }

        if (queueHasDocuments) {
            if (this.pddjDocumentQueue.length > 1) {
                return `Printer Queue: ${this.pddjDocumentQueue.length} Documents Pending`;
            } else {
                return `Printer Queue: ${this.pddjDocumentQueue.length} Document Pending`;
            }
        } else {
            return 'Printer Queue: No Pending Documents';
        }
    }

    updatePddjQueueCount(updateDocumentCounter: boolean) {
        if (updateDocumentCounter) {
            this.pddjDocumentQueueSessionCount++;
        }

        this.pddjQueueVerbiage();
    }

    getSignatureMetaData$(dealId: any, documentId: any): Observable<[{ [key: string]: PdfField }]> {
        const signatureMetaData: [{ [key: string]: PdfField }] = [{}];
        if (!this.apiSigningEnabled) {
            return of(signatureMetaData);
        }

        return this.apiService
            .getV2$<{
                buyerSigningMetaData?: string;
                cobuyerSigningMetaData?: string;
                dealerSigningMetaData?: string;
                dealFieldsSigningMetaData?: string;
            }>(`api/Document/signatureMetaData?dealerId=${this.userService.user.dealerId}&dealId=${dealId}&documentId=${documentId}`)
            .pipe(
                map((response) => {
                    if (response?.data?.buyerSigningMetaData !== null && response?.data?.buyerSigningMetaData !== undefined) {
                        const buyerMetaData: { [key: string]: PdfField } = JSON.parse(response.data.buyerSigningMetaData);
                        signatureMetaData.push(buyerMetaData);
                    }

                    if (response?.data?.cobuyerSigningMetaData !== null && response?.data?.cobuyerSigningMetaData !== undefined) {
                        const cobuyerMetaData: { [key: string]: PdfField } = JSON.parse(response.data.cobuyerSigningMetaData);
                        signatureMetaData.push(cobuyerMetaData);
                    }

                    if (response?.data?.dealerSigningMetaData !== null && response?.data?.dealerSigningMetaData !== undefined) {
                        const dealerMetaData: { [key: string]: PdfField } = JSON.parse(response.data.dealerSigningMetaData);
                        signatureMetaData.push(dealerMetaData);
                    }

                    if (response?.data?.dealFieldsSigningMetaData !== null && response?.data?.dealFieldsSigningMetaData !== undefined) {
                        const dealFieldsMetaData: { [key: string]: PdfField } = JSON.parse(response.data.dealFieldsSigningMetaData);
                        signatureMetaData.push(dealFieldsMetaData);
                    }

                    // TODO: figure out why this is here...
                    signatureMetaData.splice(0, 1);
                    return signatureMetaData;
                })
            );
    }

    checkForValidDocument(dealId: number, dealerId: number, documentId: number, hash: string, hashType: string): Observable<boolean> {
        const request: HashCompareRequest = {
            dealId: dealId,
            dealerId: dealerId,
            documentId: documentId,
            hash: hash,
            hashType: hashType,
        };

        return this.apiService
            .postV2$('api/Document/ValidDocument', JSON.stringify(request), {
                AccessControlOrigin: '*',
                'Content-Type': 'application/json',
            })
            .pipe(
                catchError((httpResponse) => {
                    console.error(httpResponse?.error);
                    return of(httpResponse);
                }),
                map((response) => {
                    return response.data;
                })
            );
    }

    getStandardDocuments(dealerId: number): Observable<StandardDocument[]> {
        return this.apiService.getV2$<StandardDocument[]>(`api/Document/standardDocuments?dealerId=${dealerId}`).pipe(
            map((response) => {
                return response?.data;
            })
        );
    }

    // Only available for internal users
    reSignDocument(dealerId: number, dealId: number, documentId: number, signatureImagesOnDoc: boolean, overrideIsCertified: boolean): Observable<any> {
        return this.apiService
            .postV2$(
                'api/Document/SignSmartFix',
                {
                    dealerId: dealerId,
                    dealId: dealId,
                    documentId: documentId,
                    signatureImagesOnDoc: signatureImagesOnDoc,
                    overrideIsCertified: overrideIsCertified,
                },
                {
                    AccessControlOrigin: '*',
                    'Content-Type': 'application/json',
                }
            )
            .pipe(
                catchError((httpResponse) => {
                    console.error(httpResponse?.error);
                    return of(httpResponse);
                }),
                map((response) => {
                    return response.data;
                })
            );
    }

    // Only available for internal users
    changeDocumentStatus(documentStatus: number, dealerId: number, dealId: number, documentId: number): Observable<any> {
        return this.apiService
            .postV2$(
                'api/Document/updateStatus',
                {
                    documentStatusInt: documentStatus,
                    dealerId: dealerId,
                    dealId: dealId,
                    documentId: documentId,
                },
                {
                    AccessControlOrigin: '*',
                    'Content-Type': 'application/json',
                }
            )
            .pipe(
                catchError((httpResponse) => {
                    console.error(httpResponse?.error);
                    return of(httpResponse);
                }),
                map((response) => {
                    return response.data;
                })
            );
    }

    updateDocumentInfo(document: DdjDocument, newName: string, isDealerOnly?: boolean, updateType: string = null): Observable<boolean> {
        return this.apiService
            .putV3$<boolean>('api/deal/updateDocument', {
                dealerId: document.dealerId,
                dealId: document.dealId,
                document,
                newName,
                isDealerOnly: isDealerOnly ?? document.isDealerOnly,
                lastAction: updateType,
            })
            .pipe(map((response) => response?.data));
    }

    ngOnDestroy() {
        this.isComponentAlive = false;
    }
}
